import { useMemo } from 'react'

import { ModalContext } from '@domain-group/suit-theme'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditAgencyTeamMemberModal from '@modals/CreateOrEditAgencyTeamMemberModal'
import CreateOrEditImageLibraryModal from '@modals/CreateOrEditImageLibraryModal'
import CreateOrEditImageModal from '@modals/CreateOrEditImageModal'
import DuplicateTeamModal from '@modals/DuplicateTeamModal'

import type {
  ImageLibraryModel, ImageModel, TeamMemberModel, TeamModel,
} from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrEditAgencyTeamMemberModal: false,
  showCreateOrEditImageLibraryModal: false,
  showCreateOrEditImageModal: false,
  showDuplicateTeamModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditAgencyTeamMemberModal: {
      closeModal: () => setState({ showCreateOrEditAgencyTeamMemberModal: false }),
      createTeamMember: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateTeamMember: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditImageModal: {
      closeModal: () => setState({ showCreateOrEditImageModal: false }),
      deleteImage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      setCoverImage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditImageLibraryModal: {
      closeModal: () => setState({ showCreateOrEditImageLibraryModal: false }),
      createImageLibrary: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateImageLibrary: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    DuplicateTeamModal: {
      closeModal: () => setState({ showDuplicateTeamModal: false }),
      duplicateTeam: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }
  return componentCallbacks[component] || {}
}

const Details = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditAgencyTeamMemberModal,
    showCreateOrEditImageLibraryModal,
    showCreateOrEditImageModal,
    showDuplicateTeamModal,
  } = state

  const { teamId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      setAsDefault: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showCreateOrEditAgencyTeamMemberModal: (payload: { teamMember: Partial<TeamMemberModel> }) => {
        setModalData('CreateOrEditAgencyTeamMemberModal', payload)
        setState({ showCreateOrEditAgencyTeamMemberModal: true })
      },
      showCreateOrEditImageLibraryModal: (payload: { imageLibrary: Partial<ImageLibraryModel> }) => {
        setModalData('CreateOrEditImageLibraryModal', payload)
        setState({ showCreateOrEditImageLibraryModal: true })
      },
      showCreateOrEditImageModal: (payload: { image: ImageModel }) => {
        setModalData('CreateOrEditImageModal', payload)
        setState({ showCreateOrEditImageModal: true })
      },
      showDuplicateTeamModal: (payload: { team: TeamModel }) => {
        setModalData('DuplicateTeamModal', payload)
        setState({ showDuplicateTeamModal: true })
      },
      updateTeam: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent teamId={teamId} />

        <CreateOrEditAgencyTeamMemberModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditAgencyTeamMemberModal', setState)}
          showModal={showCreateOrEditAgencyTeamMemberModal}
        />

        <CreateOrEditImageLibraryModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditImageLibraryModal', setState)}
          showModal={showCreateOrEditImageLibraryModal}
        />

        <CreateOrEditImageModal
          callbacks={callbacks('CreateOrEditImageModal', setState)}
          showModal={showCreateOrEditImageModal}
        />

        <DuplicateTeamModal
          // @ts-expect-error TS2322
          callbacks={callbacks('DuplicateTeamModal', setState)}
          showModal={showDuplicateTeamModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
